import React from 'react';
import { Snackbar as MuiSnackbar, Alert } from '@mui/material';

export type SnackbarType = 'success' | 'error' | 'warning' | 'info' | 'default';

interface CustomSnackbarProps {
  open: boolean;
  message: string;
  type?: SnackbarType;
  duration?: number;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  onClose: () => void;
}

const CustomSnackbar: React.FC<CustomSnackbarProps> = ({
  open,
  message,
  type = 'success',
  duration = 1500,
  position = { vertical: 'bottom', horizontal: 'center' },
  onClose
}) => {
  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    onClose();
  };

  return (
    <MuiSnackbar
      open={open}
      autoHideDuration={duration}
      onClose={handleClose}
      anchorOrigin={position}
      sx={{
        '& .MuiAlert-root': {
          minWidth: '300px',
          fontSize: '14px',
          fontWeight: 500,
        }
      }}
    >
      <Alert 
        onClose={type === 'default' ? undefined : handleClose} 
        severity={type === 'default' ? 'info' : type}
        variant="filled"
        icon={type === 'default' ? false : undefined}
        sx={{
          width: '75%',
          '& .MuiAlert-message': {
            display: 'flex',
            alignItems: 'center',
            padding: '6px 16px',
            alignSelf: 'stretch',
            borderRadius: '4px',
          },
          // Custom colors for different types
          ...(type === 'success' && {
            backgroundColor: '#4caf50', // Green
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            }
          }),
          ...(type === 'error' && {
            backgroundColor: '#f44336', // Red
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            }
          }),
          ...(type === 'warning' && {
            backgroundColor: '#ff9800', // Orange
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            }
          }),
          ...(type === 'info' && {
            backgroundColor: '#2196f3', // Blue
            color: '#ffffff',
            '& .MuiAlert-icon': {
              color: '#ffffff',
            }
          }),
          ...(type === 'default' && {
            backgroundColor: '#323232', // Black
            color: '#ffffff',
            fontWeight: 300, // Lighter font weight for default type
            borderRadius: '4px',
            display: 'flex',
            padding: '6px 16px',
            alignItems: 'center',
            alignSelf: 'stretch',
            '& .MuiAlert-message': {
              fontWeight: 300, // Ensure message text is also lighter
              fontSize: '16px', // Smaller font size for default type
            },
            '& .MuiAlert-icon': {
              color: '#ffffff',
            }
          }),
        }}
      >
        {message}
      </Alert>
    </MuiSnackbar>
  );
};

export default CustomSnackbar;